import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Download, Upload, ChevronDown } from 'lucide-react'
import { FileUploadDialog } from '@/components/ui/file-upload-dialog'
import { useToast } from '@/hooks/use-toast'
import { useEntityImport, useEntityExport } from '@/features/data-management/hooks'

interface ImportExportButtonsProps {
    /** Entity type key — e.g. 'domains', 'questions', 'users', 'all' */
    entityType: string
    /** Human-readable label for toasts/dialogs — defaults to entityType */
    entityLabel?: string
    /** Callback fired after a successful import (e.g. to refetch queries) */
    onImportSuccess?: () => void
}

export function ImportExportButtons({
    entityType,
    entityLabel,
    onImportSuccess,
}: ImportExportButtonsProps) {
    const [importOpen, setImportOpen] = useState(false)
    const [exportMenuOpen, setExportMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const { toast } = useToast()
    const importMutation = useEntityImport(entityType)
    const triggerExport = useEntityExport()

    const label = entityLabel ?? entityType.replace(/_/g, ' ')

    // Close dropdown on outside click
    useEffect(() => {
        if (!exportMenuOpen) return
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [exportMenuOpen])

    const handleImport = async (files: File[]) => {
        try {
            const result = await importMutation.mutateAsync(files)
            const data = result?.data ?? result
            toast({
                title: 'Import Complete',
                description: `Created: ${data?.created ?? 0}, Updated: ${data?.updated ?? 0}${data?.errors?.length ? `, Errors: ${data.errors.length}` : ''}`,
            })
            setImportOpen(false)
            onImportSuccess?.()
        } catch (err: any) {
            toast({
                title: 'Import Failed',
                description: err?.response?.data?.error || err?.message || 'Something went wrong.',
                variant: 'destructive',
            })
        }
    }

    const handleExport = (format: 'json' | 'csv') => {
        setExportMenuOpen(false)
        triggerExport(entityType, format)
    }

    return (
        <>
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
                    <Upload className="h-4 w-4 mr-1" />
                    Import
                </Button>

                {/* Split export button */}
                <div className="relative" ref={menuRef}>
                    <div className="flex">
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-r-none border-r-0"
                            onClick={() => handleExport('json')}
                        >
                            <Download className="h-4 w-4 mr-1" />
                            Export
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-l-none px-1.5"
                            onClick={() => setExportMenuOpen(prev => !prev)}
                        >
                            <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                    {exportMenuOpen && (
                        <div className="absolute right-0 mt-1 z-50 min-w-[120px] rounded-md border bg-popover p-1 shadow-md">
                            <button
                                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                                onClick={() => handleExport('json')}
                            >
                                Export as JSON
                            </button>
                            <button
                                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                                onClick={() => handleExport('csv')}
                            >
                                Export as CSV
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <FileUploadDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                title={`Import ${label}`}
                description={`Upload CSV or JSON files to import ${label}.`}
                onUpload={handleImport}
                isUploading={importMutation.isPending}
            />
        </>
    )
}
